// quick-view-modal.js

// ==========================================
// 1. Define Web Component (Using Fixed DIV instead of Dialog)
// ==========================================
class CustomModalLS extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
  }

  connectedCallback() {
    this.render();
    this.overlay = this.shadowRoot.querySelector('.modal-overlay');
    this.bindEvents();
  }

  disconnectedCallback() {
    this.unbindEvents();
  }

  render() {
    this.shadowRoot.innerHTML = `
      <style>
        .modal-overlay {
          position: fixed;
          inset: 0;
          z-index: 9 ; /* Set lower than typical Cart Drawer (usually 9999) */
          display: flex;
          align-items: center;
          justify-content: center;
          visibility: hidden;
          opacity: 0;
          transition: opacity 0.3s ease, visibility 0.3s ease;
        }

        .modal-overlay.is-open {
          visibility: visible;
          opacity: 1;
        }

        .modal-backdrop {
          position: absolute;
          inset: 0;
          background: rgba(0, 0, 0, 0.4);
          backdrop-filter: blur(8px);
          -webkit-backdrop-filter: blur(8px);
        }
        
        .modal-window {
          position: relative;
          background: #fff;
          border-radius: 12px;
          width: 100%;
          max-width: 95%; 
          box-shadow: 0 20px 40px rgba(0,0,0,0.15);
          overflow: hidden;
          z-index: 1;
          transform: translateY(20px) scale(0.98);
          transition: transform 0.3s cubic-bezier(0.16, 1, 0.3, 1);
        }

        .modal-overlay.is-open .modal-window {
          transform: translateY(0) scale(1);
        }

        /* Floating Close Button */
        .close-btn {
          position: absolute;
          top: 15px;
          right: 15px;
          z-index: 99;
          background: #fff;
          border: 1px solid #eee;
          border-radius: 50%;
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          box-shadow: 0 4px 12px rgba(0,0,0,0.08);
          color: #333;
          transition: all 0.2s ease;
        }
        
        .close-btn:hover {
          background: #f8f8f8;
          transform: scale(1.05);
          color: #000;
        }

        .close-btn svg {
          width: 20px;
          height: 20px;
        }
        
        .modal-body {
          padding: 0; 
          overflow-y: auto;
          max-height: 90vh;
          scrollbar-width: none;
        }
        
        .modal-body::-webkit-scrollbar {
          display: none;
        }
        
        ::slotted(*) {
          width: 100% !important;
          max-width: 100% !important;
        }
      </style>

      <div class="modal-overlay">
        <div class="modal-backdrop"></div>
        <div class="modal-window" role="dialog" aria-modal="true">
          <button class="close-btn" aria-label="Close">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
          </button>
          <div class="modal-body">
            <slot name="content"></slot>
          </div>
        </div>
      </div>
    `;
  }

  bindEvents() {
    this.closeBtn = this.shadowRoot.querySelector('.close-btn');
    this.backdrop = this.shadowRoot.querySelector('.modal-backdrop');
    
    this.closeHandler = () => this.close();
    
    this.closeBtn.addEventListener('click', this.closeHandler);
    this.backdrop.addEventListener('click', this.closeHandler);
  }

  unbindEvents() {
    if(this.closeBtn) this.closeBtn.removeEventListener('click', this.closeHandler);
    if(this.backdrop) this.backdrop.removeEventListener('click', this.closeHandler);
  }

  open() {
    if (this.overlay && !this.overlay.classList.contains('is-open')) {
      document.body.style.overflow = 'hidden'; // Prevent background scrolling
      this.overlay.classList.add('is-open');
    }
  }

  close() {
    if (this.overlay && this.overlay.classList.contains('is-open')) {
      this.overlay.classList.remove('is-open');
      document.body.style.overflow = '';
      
      // Wait for CSS transition to finish before clearing DOM
      setTimeout(() => {
        const contentSlot = document.getElementById('global-modal-content');
        if(contentSlot) contentSlot.innerHTML = '';
      }, 300);
      
      this.dispatchEvent(new CustomEvent('modal-closed', { 
        bubbles: true, 
        composed: true 
      }));
    }
  }
}

if (!customElements.get('custom-modal-l-s')) {
  customElements.define('custom-modal-l-s', CustomModalLS);
}

// ==========================================
// 2. Global Event Listener & Fetch Logic
// ==========================================
document.addEventListener('click', async (event) => {
  const triggerBtn = event.target.closest('.js-global-modal-trigger');
  if (!triggerBtn) return;

  event.preventDefault();

  const requestUrl = triggerBtn.getAttribute('data-ajax-url') || triggerBtn.getAttribute('href');
  const sectionId = triggerBtn.getAttribute('data-section-id') || 'main-product';

  if (!requestUrl) return;

  let globalModal = document.getElementById('global-ajax-modal');
  if (!globalModal) {
    globalModal = document.createElement('custom-modal-l-s');
    globalModal.id = 'global-ajax-modal';
    globalModal.innerHTML = `
      <div slot="content" id="global-modal-content"></div>
    `;
    document.body.appendChild(globalModal);
  }

  const modalContent = document.getElementById('global-modal-content');

  modalContent.innerHTML = `
    <div style="display: flex; justify-content: center; align-items: center; min-height: 300px; color: #666;">
      <span>Loading...</span>
    </div>
  `;

  // Small delay to ensure DOM is ready before adding transition class
  requestAnimationFrame(() => {
    globalModal.open();
  });

  const urlObj = new URL(requestUrl, window.location.origin);
  
  try {
    const response = await fetch(urlObj.toString());
    if (!response.ok) throw new Error('Network response was not ok');
    
    const htmlText = await response.text();
    const parser = new DOMParser();
    const htmlDocument = parser.parseFromString(htmlText, 'text/html');
    
    let mainSection = htmlDocument.querySelector(`[id*="${sectionId}"]`) || 
                      htmlDocument.querySelector('[id*="__main"]') || 
                      htmlDocument.querySelector('.shopify-section:has(.product)');
                      
    if (mainSection) {
      modalContent.innerHTML = mainSection.outerHTML;
    } else {
      modalContent.innerHTML = htmlText;
    }

  } catch (error) {
    console.error('Failed to load modal content:', error);
    modalContent.innerHTML = `
      <div style="text-align:center; padding: 40px; color: #d9534f;">
        Failed to load. Please check your connection or try again.
      </div>
    `;
  }
});